/**
 * Corpi delle richieste Meta (POST /<phone_number_id>/messages) per le azioni
 * dei numeri "Ufficiale" che prima non facevano nulla: reazioni, posizione,
 * contatto, segna come letto e indicatore "sta scrivendo".
 *
 * Formati verificati sulla documentazione Meta (Cloud API, settembre 2026):
 *  - reazione:   { type:'reaction', reaction:{ message_id, emoji } }
 *                (emoji vuota per togliere la reazione; il webhook in arrivo
 *                segnala la rimozione omettendo emoji)
 *  - posizione:  { type:'location', location:{ latitude, longitude, name?, address? } }
 *  - contatto:   { type:'contacts', contacts:[{ name:{ formatted_name }, phones:[{ phone }] }] }
 *  - letto:      { status:'read', message_id }
 *  - scrive:     { status:'read', message_id, typing_indicator:{ type:'text' } }
 *                (sparisce alla risposta o dopo 25 secondi)
 *
 * Modulo puro: nessuna chiamata di rete, testato in __tests__/cloudPayloads.test.ts.
 */

import { normalizeWhatsAppDigits } from './phoneDigits';
export type GraphBody = Record<string, unknown>;

const BASE = { messaging_product: 'whatsapp', recipient_type: 'individual' } as const;

/** Ogni quanto rimandare "sta scrivendo" mentre l'operatore scrive (Meta lo tiene 25 s). */
export const TYPING_INTERVAL_MS = 20_000;

export function buildReactionBody(to: string, messageId: string, emoji: string): GraphBody {
  if (!to) throw new Error('Destinatario mancante');
  if (!messageId) throw new Error('Messaggio da cui togliere o a cui mettere la reazione mancante');
  return { ...BASE, to, type: 'reaction', reaction: { message_id: messageId, emoji: emoji || '' } };
}

export function buildLocationBody(
  to: string,
  latitude: number,
  longitude: number,
  name?: string,
  address?: string,
): GraphBody {
  if (!to) throw new Error('Destinatario mancante');
  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90
    || !Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    throw new Error('Coordinate della posizione non valide');
  }
  const location: Record<string, unknown> = { latitude, longitude };
  if (name?.trim()) location.name = name.trim();
  if (address?.trim()) location.address = address.trim();
  return { ...BASE, to, type: 'location', location };
}

export function buildContactBody(to: string, contact: { name: string; phone: string }): GraphBody {
  if (!to) throw new Error('Destinatario mancante');
  const name = contact.name?.trim();
  // Numeri salvati senza prefisso diventano italiani (come per i numeri Standard).
  const digits = normalizeWhatsAppDigits(contact.phone);
  if (!name) throw new Error('Il contatto deve avere un nome');
  if (digits.length < 6) throw new Error('Il contatto deve avere un numero di telefono valido');
  return {
    ...BASE,
    to,
    type: 'contacts',
    contacts: [{ name: { formatted_name: name }, phones: [{ phone: `+${digits}` }] }],
  };
}

export function buildReadBody(messageId: string): GraphBody {
  return { messaging_product: 'whatsapp', status: 'read', message_id: messageId };
}

export function buildTypingBody(messageId: string): GraphBody {
  return { ...buildReadBody(messageId), typing_indicator: { type: 'text' } };
}

/** true se e' il momento di rimandare "sta scrivendo" per quella chat. */
export function shouldSendTyping(lastSentAt: number | undefined, now: number, intervalMs = TYPING_INTERVAL_MS): boolean {
  return lastSentAt === undefined || now - lastSentAt >= intervalMs;
}

/** Chi reagisce a nome dell'azienda nella tabella whatsapp_message_reactions (come il webhook). */
export const BUSINESS_REACTION_SENDER = 'me';

export interface ReactionSummary {
  emoji: string;
  count: number;
  senderJids: string[];
}

/**
 * Reazioni di un messaggio dopo che l'azienda ne mette una (o la toglie con
 * emoji vuota), per l'aggiornamento immediato della bolla. Una sola reazione
 * per mittente, come su WhatsApp.
 */
export function applyReaction(
  reactions: ReactionSummary[] | undefined,
  emoji: string,
  senderJid: string = BUSINESS_REACTION_SENDER,
): ReactionSummary[] {
  const out: ReactionSummary[] = [];
  for (const r of reactions ?? []) {
    const senders = r.senderJids.filter((s) => s !== senderJid);
    if (senders.length > 0) out.push({ ...r, senderJids: senders, count: senders.length });
  }
  if (!emoji) return out;
  const existing = out.find((r) => r.emoji === emoji);
  if (existing) {
    existing.senderJids = [...existing.senderJids, senderJid];
    existing.count = existing.senderJids.length;
  } else {
    out.push({ emoji, count: 1, senderJids: [senderJid] });
  }
  return out;
}
